import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { repeaterToArray } from "./commercialProjectAcf";
import { normalizeMaterialRows } from "./engineeringMaterialsTotals";

export const MATERIALS_JSON_FIELD_ID = "engineering_details.material_totals";

export const MATERIALS_JSON_HEADER_ALIASES = [
  "material_totals",
  "materials_json",
  "engineering_material_totals",
  "engineering_details_material_totals",
  "material_totals_json",
];

export function isMaterialsJsonImportFieldId(fieldId) {
  return fieldId === MATERIALS_JSON_FIELD_ID;
}

function normalizeMaterialImportRow(row) {
  if (row == null || typeof row !== "object" || Array.isArray(row)) return null;
  const item = row.item != null ? String(row.item).trim() : "";
  const slug = row.slug != null ? String(row.slug).trim() : "";
  const quantity = row.quantity != null ? String(row.quantity).trim() : "";
  if (!item && !slug && !quantity) return null;
  const out = { item, quantity };
  if (slug) out.slug = slug;
  return out;
}

function unwrapMaterialsJsonCell(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    const inner = s.slice(1, -1).replace(/""/g, '"').trim();
    if (inner.startsWith("{") || inner.startsWith("[")) return inner;
  }
  if (s.length >= 2 && s.startsWith("'") && s.endsWith("'")) {
    const inner = s.slice(1, -1).trim();
    if (inner.startsWith("{") || inner.startsWith("[")) return inner;
  }
  return s;
}

function tryParseMaterialsJson(raw) {
  const s = unwrapMaterialsJsonCell(raw);
  if (!s) return null;
  try {
    let parsed = JSON.parse(s);
    if (typeof parsed === "string") {
      const inner = unwrapMaterialsJsonCell(parsed);
      if (inner) parsed = JSON.parse(inner);
    }
    return parsed;
  } catch {
    return null;
  }
}

function sortRepeaterKeys(keys) {
  return [...keys].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });
}

function rowsFromParsedJson(parsed) {
  if (Array.isArray(parsed)) {
    return parsed.map(normalizeMaterialImportRow).filter(Boolean);
  }
  if (parsed && typeof parsed === "object") {
    return sortRepeaterKeys(Object.keys(parsed))
      .map((k) => normalizeMaterialImportRow(parsed[k]))
      .filter(Boolean);
  }
  return [];
}

/**
 * Parse a CSV cell containing material totals JSON (numeric-key object or array of rows).
 * @returns {object[]|undefined}
 */
export function parseMaterialsJsonFromCsvCell(raw) {
  if (raw == null) return undefined;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const rows = rowsFromParsedJson(raw);
    return rows.length ? rows : undefined;
  }
  const s = typeof raw === "string" ? raw.trim() : raw;
  if (s === "") return undefined;

  const parsed = typeof s === "string" ? tryParseMaterialsJson(s) : s;
  if (parsed == null) return undefined;

  const rows = rowsFromParsedJson(parsed);
  return rows.length ? rows : undefined;
}

export function coerceMaterialsJsonImportValue(fieldId, raw) {
  if (!isMaterialsJsonImportFieldId(fieldId)) return undefined;
  return parseMaterialsJsonFromCsvCell(raw);
}

/** Attach imported material totals to the first engineering permit. */
export function applyMaterialTotalsToFirstPermit(domains, materialRows) {
  const normalized = normalizeMaterialRows(materialRows);
  if (!normalized.length) return domains;

  const engKey = COMMERCIAL_DOMAINS.ENGINEERING;
  const eng = { ...(domains?.[engKey] || {}) };
  const permits = repeaterToArray(eng.permits);
  while (permits.length < 1) permits.push({});
  permits[0] = {
    ...(permits[0] || {}),
    materials: normalized.map((row) => ({
      slug: row.slug || "",
      item: row.item,
      quantity: row.quantity,
    })),
  };
  return {
    ...domains,
    [engKey]: { ...eng, permits },
  };
}

export function formatMaterialsImportPreviewValue(rows) {
  if (!Array.isArray(rows) || !rows.length) return "—";
  const items = rows
    .map((r) => (r?.item != null ? String(r.item).trim() : ""))
    .filter(Boolean);
  if (items.length) {
    const preview = items.slice(0, 3).join(", ");
    return `${rows.length} material(s): ${preview}${items.length > 3 ? "…" : ""}`;
  }
  return `${rows.length} material(s)`;
}
